import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
} from "@/components/ui/alert-dialog";
import { UserPlus, CheckCircle, Clock, Loader2 } from "lucide-react";
import { useProfile } from "@/hooks/useProfile";
import { useHasRole } from "@/hooks/useUserRoles";
import {
  useExistingMentorshipRequest,
  useCreateMentorInitiatedRequest,
} from "@/hooks/useMentorshipRequests";
import { useMentorTopics } from "@/hooks/useMentorTopics";

interface MentorInitiatedRequestButtonProps {
  entrepreneurId: string;
  entrepreneurName: string;
  entrepreneurAvatarUrl?: string | null;
  size?: "default" | "sm";
}

export function MentorInitiatedRequestButton({
  entrepreneurId,
  entrepreneurName,
  size = "default",
}: MentorInitiatedRequestButtonProps) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [message, setMessage] = useState("");

  const { data: profile } = useProfile();
  const {
    data: existingRequest,
    isLoading: requestLoading,
  } = useExistingMentorshipRequest(entrepreneurId);
  const { data: topics } = useMentorTopics();
  const createRequest = useCreateMentorInitiatedRequest();

  const isMentor = profile?.role === "mentor";
  const isAdminRole = useHasRole("admin");
  const isAdmin = profile?.role === "admin" || isAdminRole;

  if (!isMentor && !isAdmin) return null;

  if (requestLoading) {
    return (
      <Button variant="outline" size={size} disabled>
        <Loader2 className="h-4 w-4 animate-spin" />
      </Button>
    );
  }

  if (existingRequest && !isAdmin) {
    if (existingRequest.status === "accepted") {
      return null;
    }
    return (
      <Button variant="outline" size={size} disabled className="text-muted-foreground">
        <Clock className="h-4 w-4 mr-1" />
        Request Sent
      </Button>
    );
  }

  const handleSubmit = async () => {
    try {
      await createRequest.mutateAsync({
        entrepreneurId,
        topicId: topics?.[0]?.id || null,
        message: message.trim() || "",
        isAdmin: !!isAdmin,
      });
      setMessage("");
      setDialogOpen(false);
    } catch {
      // handled in mutation
    }
  };

  const handleClose = () => {
    setMessage("");
    setDialogOpen(false);
  };

  return (
    <>
      <Button
        variant="outline"
        size={size}
        onClick={() => setDialogOpen(true)}
      >
        <UserPlus className="h-4 w-4 mr-1" />
        {isAdmin ? "Begin Mentorship" : "Offer Mentorship"}
      </Button>

      <AlertDialog open={dialogOpen} onOpenChange={handleClose}>
        <AlertDialogContent className="sm:max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle>Request Mentorship with {entrepreneurName}?</AlertDialogTitle>
            <AlertDialogDescription>
              Send a mentorship offer to {entrepreneurName}.
              {isAdmin && " As admin, this will be instantly accepted."}
            </AlertDialogDescription>
          </AlertDialogHeader>

          <div className="py-2">
            <Textarea
              placeholder="Add a note (optional)"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              className="min-h-[80px]"
            />
          </div>

          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleClose}>Cancel</AlertDialogCancel>
            <Button
              onClick={handleSubmit}
              disabled={createRequest.isPending}
            >
              {createRequest.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                  Sending…
                </>
              ) : (
                "Send Request"
              )}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
